import { useState } from "react";
import { Link } from "react-router";

import { Badge } from "@/components/badge";
import { type Column, DataTable } from "@/components/data-table";
import { DetailPanel, KeyValueList } from "@/components/detail-panel";
import { Button, Field, TextInput } from "@/components/field";
import { plausibilityVerificationPath } from "@/constants/routes";
import { useOrgSelection } from "@/context/use-org-selection";
import {
	type ConfusionCounts,
	type ReplayReport,
	useReplay,
} from "@/features/plausibility/use-replay";

import { formatPercent } from "./format";

const EXAMPLE = JSON.stringify(
	{
		lr: { strengthToLogLr: { strong: 1.2 } },
		severityPolicy: { ceilings: { lowPriorityLog: 3 } },
	},
	null,
	2,
);

const DECISIONS = ["escalate", "alertNow", "reviewQueue", "lowPriorityLog"];

/**
 * Try a configuration change against every stored verification before
 * shipping it. No model calls: the rows hold the observations, the prior,
 * and the costs, so only the arithmetic runs again.
 */
export const Replay = () => {
	const { selected } = useOrgSelection();
	const replay = useReplay();
	const [classId, setClassId] = useState("");
	const [overrides, setOverrides] = useState(EXAMPLE);
	const [parseError, setParseError] = useState<null | string>(null);

	const run = () => {
		let parsed: Record<string, unknown> = {};
		try {
			parsed = overrides.trim()
				? (JSON.parse(overrides) as Record<string, unknown>)
				: {};
			setParseError(null);
		} catch (error) {
			setParseError(error instanceof Error ? error.message : "Invalid JSON");
			return;
		}
		replay.mutate({ classId: classId || undefined, overrides: parsed });
	};

	return (
		<div className="flex flex-col gap-4">
			<p className="text-secondary text-sm">
				Re-scores {selected?.name ?? "the selected organization"}'s completed
				verifications under the overrides below, on top of the current global
				config. Overrides accept <code>lr</code>, <code>decision</code>,{" "}
				<code>severityPolicy</code>, <code>costs</code>, and{" "}
				<code>logPriorOdds</code>.
			</p>
			<form
				className="flex flex-col gap-3"
				onSubmit={(event) => {
					event.preventDefault();
					run();
				}}
			>
				<div className="flex flex-wrap items-end gap-3">
					<Field htmlFor="replay-class" label="Class id (optional)">
						<TextInput
							id="replay-class"
							onChange={(e) => setClassId(e.target.value)}
							placeholder="animal.wildlife"
							value={classId}
						/>
					</Field>
					<Button disabled={replay.isPending} type="submit">
						{replay.isPending ? "Replaying…" : "Replay"}
					</Button>
				</div>
				<label
					className="flex flex-col gap-1 text-sm"
					htmlFor="replay-overrides"
				>
					<span className="text-secondary text-xs font-medium">
						Overrides (JSON)
					</span>
					<textarea
						className="border-secondary text-primary h-40 rounded-md border bg-transparent p-2 font-mono text-xs"
						id="replay-overrides"
						onChange={(e) => setOverrides(e.target.value)}
						value={overrides}
					/>
				</label>
				{(parseError || replay.error) && (
					<p className="text-error-primary text-sm" role="alert">
						{parseError ?? replay.error?.message}
					</p>
				)}
			</form>

			{replay.data && <Report report={replay.data} />}
		</div>
	);
};

const Report = ({ report }: { report: ReplayReport }) => {
	const flipColumns: Column<ReplayReport["flips"][number]>[] = [
		{
			header: "Verification",
			key: "id",
			render: (f) => (
				<Link
					className="underline"
					to={plausibilityVerificationPath(f.verificationId)}
				>
					{f.verificationId.slice(0, 8)}
				</Link>
			),
		},
		{ header: "Class", key: "class", render: (f) => f.classId },
		{
			header: "Recorded",
			key: "from",
			render: (f) => <Badge value={f.from} />,
		},
		{ header: "Proposed", key: "to", render: (f) => <Badge value={f.to} /> },
		{
			header: "Posterior",
			key: "posterior",
			render: (f) =>
				`${formatPercent(f.posteriorFrom)} → ${formatPercent(f.posteriorTo)}`,
		},
	];

	return (
		<div className="flex flex-col gap-4">
			<DetailPanel title="Coverage">
				<KeyValueList
					items={[
						{ label: "Scanned", value: String(report.scanned) },
						{ label: "Replayed", value: String(report.replayed) },
						{ label: "Skipped", value: String(report.skipped) },
						{
							label: "With verdicts",
							value: String(report.confusion.verdicted),
						},
						...(report.truncated
							? [
									{
										label: "Note",
										value: "Row cap reached; narrow the filter.",
									},
								]
							: []),
					]}
				/>
			</DetailPanel>

			<DetailPanel title="Decisions">
				<table className="text-sm">
					<thead className="text-tertiary">
						<tr>
							<th className="pr-4 text-left font-medium">Decision</th>
							<th className="pr-4 text-right font-medium">Recorded</th>
							<th className="text-right font-medium">Proposed</th>
						</tr>
					</thead>
					<tbody className="text-primary">
						{DECISIONS.map((d) => (
							<tr key={d}>
								<td className="pr-4">{d}</td>
								<td className="pr-4 text-right">
									{report.decisions.recorded[d] ?? 0}
								</td>
								<td className="text-right">
									{report.decisions.proposed[d] ?? 0}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</DetailPanel>

			{report.confusion.verdicted > 0 && (
				<DetailPanel title="Against verdicts (alerted vs. real)">
					<div className="grid grid-cols-2 gap-6">
						<Confusion counts={report.confusion.recorded} title="Recorded" />
						<Confusion counts={report.confusion.proposed} title="Proposed" />
					</div>
				</DetailPanel>
			)}

			<DetailPanel
				title={`Flips (${report.flips.length}${report.flips.length === 200 ? "+" : ""})`}
			>
				{report.flips.length === 0 ? (
					<p className="text-secondary text-sm">No decision would change.</p>
				) : (
					<DataTable
						columns={flipColumns}
						rowKey={(f) => f.verificationId}
						rows={report.flips}
					/>
				)}
			</DetailPanel>
		</div>
	);
};

const Confusion = ({
	counts,
	title,
}: {
	counts: ConfusionCounts;
	title: string;
}) => {
	const precision =
		counts.tp + counts.fp ? counts.tp / (counts.tp + counts.fp) : 0;
	const recall =
		counts.tp + counts.fn ? counts.tp / (counts.tp + counts.fn) : 0;
	return (
		<div>
			<h4 className="text-primary mb-2 text-sm font-medium">{title}</h4>
			<KeyValueList
				items={[
					{ label: "Alerted, real", value: String(counts.tp) },
					{ label: "Alerted, not real", value: String(counts.fp) },
					{ label: "Quiet, real (missed)", value: String(counts.fn) },
					{ label: "Quiet, not real", value: String(counts.tn) },
					{ label: "Precision", value: formatPercent(precision) },
					{ label: "Recall", value: formatPercent(recall) },
				]}
			/>
		</div>
	);
};
